import { useEffect, useState } from "react"

import { useParams } from "react-router-dom"



import {

  useRateConOrgProperties,

  useUpdateRateConOrgProperties,

} from "@/management/hooks/useRateConOrgProperties"

import { OrgProperties, RouteParams } from "@/shared/types"

import {

  getOrgPropertyRawValue,

  getOrgPropertyStringValue,

  parseRateConIntegrationConfig,

  RATE_CON_INTEGRATION_HTTP_METHODS,

  RateConIntegrationHttpMethod,

  serializeRateConIntegrationConfig,

  showToast,

  useIsSysAdmin,

} from "@/shared/utils"

import { styled } from "@/stitches/config"



import {

  Box,

  Button,

  Copy,

  Flex,

  FormField,

  FormInput,

  FormSelect,

  Redacted,

  Spacer,

  Stack,

  Switch,

  SwitchOption,

  Title,

} from "@/shared/components"

import { SFormFieldElement } from "@/shared/components/forms/FormField/FormField.styles"



const IntegrationPayloadTextarea = styled("textarea", SFormFieldElement, {

  minHeight: "$128",

  resize: "vertical",

})



export const OrganizationRateConSettings = () => {

  const { organizationId } = useParams<keyof RouteParams>() as RouteParams

  const isSysAdmin = useIsSysAdmin()

  const { data: properties, isLoading } = useRateConOrgProperties(organizationId)

  const { mutate: saveProperties, isLoading: isSaving } = useUpdateRateConOrgProperties(organizationId)



  const [emailEnabled, setEmailEnabled] = useState(false)

  const [disableCriticalAutoWinner, setDisableCriticalAutoWinner] = useState(false)

  const [rateConEmail, setRateConEmail] = useState("")

  const [rateConEmailSender, setRateConEmailSender] = useState("")

  const [confirmationTimeoutMinutes, setConfirmationTimeoutMinutes] = useState("30")

  const [integrationUrl, setIntegrationUrl] = useState("")

  const [integrationMethod, setIntegrationMethod] = useState<RateConIntegrationHttpMethod>("GET")

  const [integrationAuthHeaderName, setIntegrationAuthHeaderName] = useState("")

  const [integrationAuthHeaderValue, setIntegrationAuthHeaderValue] = useState("")

  const [integrationPayloadJson, setIntegrationPayloadJson] = useState("")



  useEffect(() => {

    if (!properties) {

      return

    }



    setEmailEnabled(
      getOrgPropertyStringValue(properties, OrgProperties.FEATURE_FLAG_RATE_CON_EMAIL_ENABLED) === "true",
    )

    setDisableCriticalAutoWinner(
      getOrgPropertyStringValue(
        properties,
        OrgProperties.FEATURE_FLAG_DISABLE_CRITICAL_AUTO_WINNER_AWARD,
      ) === "true",
    )

    setRateConEmail(getOrgPropertyStringValue(properties, OrgProperties.RATE_CON_EMAIL))

    setRateConEmailSender(getOrgPropertyStringValue(properties, OrgProperties.RATE_CON_EMAIL_SENDER))

    setConfirmationTimeoutMinutes(
      getOrgPropertyStringValue(properties, OrgProperties.RATE_CON_CONFIRMATION_TIMEOUT_MINUTES) ||
        "30",
    )

    const integrationFormState = parseRateConIntegrationConfig(
      getOrgPropertyRawValue(properties, OrgProperties.RATE_CON_INTEGRATION),
    )

    setIntegrationUrl(integrationFormState.url)

    setIntegrationMethod(integrationFormState.method)

    setIntegrationAuthHeaderName(integrationFormState.authHeaderName)

    setIntegrationAuthHeaderValue(integrationFormState.authHeaderValue)

    setIntegrationPayloadJson(integrationFormState.payloadJson)

  }, [properties])



  const handleSave = () => {

    const integrationResult = serializeRateConIntegrationConfig({

      url: integrationUrl,

      method: integrationMethod,

      authHeaderName: integrationAuthHeaderName,

      authHeaderValue: integrationAuthHeaderValue,

      payloadJson: integrationPayloadJson,

    })



    if (integrationResult.error) {

      showToast({ type: "error", text: integrationResult.error })

      return

    }



    saveProperties([

      {

        propertyKey: OrgProperties.RATE_CON_INTEGRATION,

        value: integrationResult.value,

      },

      {

        propertyKey: OrgProperties.FEATURE_FLAG_RATE_CON_EMAIL_ENABLED,

        value: String(emailEnabled),

      },

      {

        propertyKey: OrgProperties.FEATURE_FLAG_DISABLE_CRITICAL_AUTO_WINNER_AWARD,

        value: String(disableCriticalAutoWinner),

      },

      { propertyKey: OrgProperties.RATE_CON_EMAIL, value: rateConEmail },

      { propertyKey: OrgProperties.RATE_CON_EMAIL_SENDER, value: rateConEmailSender },

      {

        propertyKey: OrgProperties.RATE_CON_CONFIRMATION_TIMEOUT_MINUTES,

        value: confirmationTimeoutMinutes || "30",

      },

    ])

  }



  if (!isSysAdmin) {

    return null

  }



  if (isLoading) {

    return (

      <Box css={{ marginTop: "$32" }}>

        <Redacted height="22px" width="240px" text animated />

      </Box>

    )

  }



  return (

    <Box css={{ marginTop: "$32" }}>

      <Title as="h2" scale={6} color="theme-b-n3">

        Rate confirmation settings

      </Title>

      <Spacer size={24} />

      <Stack space={24}>

        <Box>

          <Title as="h3" scale={8} color="theme-b-n3">

            TMS integration

          </Title>

          <Spacer size={8} />

          <Copy scale={10} color="theme-n6-n5">

            Single TMS call creates and returns the rate confirmation PDF. URL and payload support placeholders

            such as {"{orderid}"}, {"{apolloOrderId}"}, {"{scac}"}, {"{usdot}"}. Leave payload empty for GET

            requests without a body.

          </Copy>

          <Spacer size={16} />

          <Stack space={16}>

            <FormInput

              label="Integration URL"

              id="integrationUrl"

              value={integrationUrl}

              onChange={(event) => setIntegrationUrl(event.target.value)}

              disabled={false}

            />



            <FormSelect
              name="integrationMethod"
              id="integrationMethod"
              label="HTTP method"
              value={integrationMethod}
              options={[...RATE_CON_INTEGRATION_HTTP_METHODS]}
              onValueChange={(value) => setIntegrationMethod(value as RateConIntegrationHttpMethod)}
            />



            <FormInput

              label="Auth header name"

              id="integrationAuthHeaderName"

              value={integrationAuthHeaderName}

              onChange={(event) => setIntegrationAuthHeaderName(event.target.value)}

              disabled={false}

            />



            <FormInput

              label="Auth header value"

              id="integrationAuthHeaderValue"

              value={integrationAuthHeaderValue}

              onChange={(event) => setIntegrationAuthHeaderValue(event.target.value)}

              disabled={false}

            />



            <FormField

              label="Request payload (JSON)"

              description="Leave empty for GET requests without a body"

              id="integrationPayload"

            >

              <IntegrationPayloadTextarea

                id="integrationPayload"

                value={integrationPayloadJson}

                onChange={(event) => setIntegrationPayloadJson(event.target.value)}

              />

            </FormField>

          </Stack>

        </Box>



        <Flex align="center" css={{ gap: "$12" }}>

          <Switch

            name="rateConEmailEnabled"

            value={emailEnabled ? "true" : "false"}

            onValueChange={(value) => setEmailEnabled(value === "true")}

            checked={emailEnabled}

            disabled={false}

          >

            <SwitchOption value={emailEnabled ? "false" : "true"} />

          </Switch>

          <Copy scale={10}>Send confirmation email to carrier</Copy>

        </Flex>



        <Flex align="center" css={{ gap: "$12" }}>

          <Switch

            name="disableCriticalAutoWinner"

            value={disableCriticalAutoWinner ? "true" : "false"}

            onValueChange={(value) => setDisableCriticalAutoWinner(value === "true")}

            checked={disableCriticalAutoWinner}

            disabled={false}

          >

            <SwitchOption value={disableCriticalAutoWinner ? "false" : "true"} />

          </Switch>

          <Copy scale={10}>CRITICAL: gather quotes until strategy end (disable auto winner award)</Copy>

        </Flex>



        <FormInput

          label="Confirmation email template"

          description="Supports {carrierEmail} placeholders"

          id="rateConEmail"

          value={rateConEmail}

          onChange={(event) => setRateConEmail(event.target.value)}

          disabled={false}

        />



        <FormInput

          label="Confirmation email sender"

          description="Override From address"

          id="rateConEmailSender"

          value={rateConEmailSender}

          onChange={(event) => setRateConEmailSender(event.target.value)}

          disabled={false}

        />



        <FormInput

          label="Confirmation timeout (minutes)"

          description="Default 30 if unset"

          id="confirmationTimeoutMinutes"

          type="number"

          value={confirmationTimeoutMinutes}

          onChange={(event) => setConfirmationTimeoutMinutes(event.target.value)}

          disabled={false}

        />



        <Flex>

          <Button

            action="primary"

            size="small"

            type="button"

            ariaLabel="Save rate confirmation settings"

            loading={isSaving}

            onClick={handleSave}

          >

            <Copy as="span" fontWeight="bold">

              Save settings

            </Copy>

          </Button>

        </Flex>

      </Stack>

    </Box>

  )

}


